<template>
  <div>
    <div class="tb-form-group">
      <label class="tb-label">URL</label>

      <div v-if="provider">
        Field mapped to data source.
      </div>

      <input
        v-else
        type="text"
        :class="{
          'tb-input': true,
          'tb-input--affiliate': isAffiliateLink,
        }"
        v-model.lazy="_url"
        :disabled="isAffiliateLink"
        data-entity="producer-editor-qrCodeUrl"
        @paste="onPaste"
      />

      <span v-show="qrCodeUrlError" class="text-danger">Invalid url format</span>
    </div>

    <template v-if="!provider">
      <div
        v-if="fanaticsEnabled"
        class="margin-bottom-md affiliate-controls"
      >
        <button
          class="tb-btn sm tb-btn-contained medium-em"
          @click="showFanaticsModal = true"
        >
          <span class="new-feature margin-right-xs">New!</span>
          Search Fanatics
        </button>
  
        <button
          class="tb-btn sm tb-btn-contained medium-em"
          @click="removeLink"
        >
          Remove Link
        </button>
  
        <div
          v-if="isAffiliateLink && Object.keys(productData).length"
          class="margin-top-md"
        >
          <label class="tb-label">
              Product
              <span
                v-if="productData.image"
                class="fas fa-image affiliate-image"
              >
                <img
                  class="affiliate-image__image"
                  :src="productData.image"
                  alt="Product image"
                >
              </span>
          </label>
          <p
            class="cursor-default"
            :data-balloon="productData.name"
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span class="ellipsis block">{{ productData.name }}</span>
          </p>
        </div>
      </div>
  
      <div v-if="shortenerEnabled" class="tb-form-group">
        <!-- TODO: Finalize copy for tooltip -->
        <label
          title="URL used by the QR Code to enable tracking of scans and more analytics."
          class="tb-label"
        >
          Short URL
  
          <span class="fas fa-info-circle" />
        </label>
  
        <div class="no-wrap--ellipsis">
          {{ generatingShortUrl ? 'Generating...' : _shortUrl }}
        </div>
  
        <div v-if="shortenerError" class="text-danger">
          {{ shortenerError }}
        </div>
      </div>
  
      <fanatics-modal
        v-model="showFanaticsModal"
        :product="productData"
        @select="selectProduct"
      />
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import FanaticsModal from './FanaticsModal.vue';

import FanaticsService from '../../services/fanatics';
import Shortener from '../../services/shortener';

const catchify = require('catchify');

const isValidUrl = (url) => {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
  } catch (e) {
    return false;
  }

  return true;
};

export default {
  name: 'QRCodeUrl',
  components: {
    FanaticsModal,
  },
  props: {
    url: {
      required: true,
      type: String,
    },
    shortUrl: {
      required: true,
      type: String,
    },
    item: {
      type: Object,
      required: true,
    },
    element: {
      type: Object,
      required: true,
    },
    provider: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [String, Number],
      required: true,
    },
    productInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      generatingShortUrl: false,
      shortenerError: null,
      shortenerEnabled: process.env.QR_CODE_SWITCH === 'ON',
      qrCodeUrlError: false,
      showFanaticsModal: false,
      settingAffiliate: false,
      ldFlags: {
        fanaticsAffiliate: this.$ld.has('affiliate-fanatics-search', false),
      },
    };
  },
  computed: {
    ...mapState({
      production(state) {
        return state.production.production;
      },
    }),
    _url: {
      get() {
        return this.url;
      },
      set(v) {
        this.$emit('update:url', v);
      },
    },
    _shortUrl: {
      get() {
        return this.shortUrl;
      },
      set(v) {
        this.$emit('update:short-url', v);
      },
    },
    _productInfo: {
      get() {
        return this.productInfo;
      },
      set(v) {
        this.$emit('update:product-info', v);
      },
    },
    fanaticsEnabled() {
      return this.ldFlags.fanaticsAffiliate;
    },
    isAffiliateLink() {
      return this.checkForAffiliate(this._url);
    },
    productData() {
      if (!Object.keys(this._productInfo).length) {
        return {};
      }

      switch (this.isAffiliateLink) {
        case 'fanatics':
          return {
            image: this.productInfo.ImageUrl,
            name: this.productInfo.Name,
            id: this.productInfo.Id,
          };
      
        default:
          return {};
      }
    },
  },
  watch: {
    async _url(newVal) {
      this.qrCodeUrlError = false;
      if (isValidUrl(newVal)) {
        const affiliateType = this.checkForAffiliate(newVal);
        if (affiliateType && !this.settingAffiliate) {
          this.settingAffiliate = true;
          this._url = await this.makeAffiliate(newVal, affiliateType);
        } else if (this.settingAffiliate) {
          // prevent infinite loop after making the affiliate link
          this.settingAffiliate = false;
          await this.shortenUrl(newVal);
        } else {
          await this.shortenUrl(newVal);
        }

      } else {
        this.qrCodeUrlError = true;
      }
    },
  },
  created() {
    this.$ld.on(this.onChangeFlags);
  },
  beforeDestroy() {
    this.$ld.off(this.onChangeFlags);
  },
  methods: {
    onChangeFlags() {
      this.ldFlags.fanaticsAffiliate = this.$ld.has('affiliate-fanatics-search', false);
    },
    async shortenUrl(url) {
      this.shortenerError = null;

      this.generatingShortUrl = true;

      const [err, resp] = await catchify(
        Shortener.getShortURL(url, {
          user: this.production.owner,
          production: {
            id: this.production.id,
            title: this.production.title,
          },
          item: this.item,
          element: this.element,
        }),
      );

      if (err) {
        // TODO: Bubble up shortener errors to global state
        // so we can show error icons on the timeline item
        this.shortenerError = err?.message || 'Failed to shorten URL';
        this.generatingShortUrl = false;
        return;
      }

      const { shortUrl } = resp.data;

      this._shortUrl = shortUrl;
      this.generatingShortUrl = false;
    },
    removeLink() {
      this._url = '';
      this._productInfo = {};
    },
    checkForAffiliate(url) {
      // skip if not enabled
      if (!this.fanaticsEnabled) return false;

      if (url.toLowerCase().includes('fanatics.')) return 'fanatics';

      return false;
    },
    async makeAffiliate(url, affiliate) {
      switch (affiliate) {
        case 'fanatics':
          return this.getFanaticsLink(url);
        default:
          return url;
      }
    },
    async getFanaticsLink(url) {
      try {
        return FanaticsService.makeAffiliateLink(url, {
          accountId: this.production.owner,
          productionId: this.production.id,
          entityId: this.entityId,
        });
      } catch (error) {
        console.error('Error creating affiliate link', error);
      }

      return url;
    },
    selectProduct(v) {
      this._url = v.Url;
      this._productInfo = v;
    },
    onPaste() {
      this._productInfo = {};
    },
  },
};
</script>

<style lang="scss">
.affiliate-controls {
  @media screen and (max-width: 1465px) {
    & > .tb-btn + .tb-btn {
      margin-left: 0;
      margin-top: 8px;
    }
  }
}

.affiliate-name {
  &:hover {
    overflow: visible;
  }
}

.affiliate-image {
  position: relative;

  &:hover > &__image {
    display: block;
  }

  &__image {
    display: none;
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    width: 100px;
    height: 100px;
  }
}
</style>
